<template>
  <div>
    <SchemaTablePage
      ref="tablePageRef"
      layout-mode="tab"
      show-summary
      :search-schema="searchSchema"
      :columns="tableColumns"
      :default-query="defaultQuery"
      :request="request"
      :build-params="buildParams"
      :parse-result="parseResult"
      :table-attrs="tableAttrs"
    >
      <template #search-amountRange="{ query }">
        <div class="amount-range">
          <el-input-number v-model="query.minOrderAmount" :controls="false" placeholder="最小金额" :min="0" />
          <div style="width: 10px" />
          <el-input-number v-model="query.maxOrderAmount" :controls="false" placeholder="最大金额" :min="0" />
        </div>
      </template>

      <template #colStatusText="{ row }">
        <el-tag size="mini">{{ row.statusText }}</el-tag>
      </template>
      <template #colMemberAccount="{ row }">
        <MemberAccountCell :row="row" :username="row.memberAccount" :uid="row.uid" :vip-level="row.memberLevel" />
      </template>
      <template #colOrderAmount="{ row }">{{ row.orderAmount | centsToYuan }}</template>
      <template #colActions="{ row }">
        <el-button v-if="row.status === 3" type="text" size="small" @click="openRiskDialog(row, true)">审核</el-button>
        <el-button v-if="canWithdrawRefund(row.status)" type="text" size="small" @click="openRefundDialog(row)">
          退款
        </el-button>
      </template>
    </SchemaTablePage>

    <RiskControlInfoDialog :visible.sync="riskDialogVisible" :row="riskRow" @audited="handleAudited" />
    <WithdrawRefundDialog :visible.sync="refundDialogVisible" :row="refundRow" @success="handleRefundSuccess" />
  </div>
</template>

<script>
import { getPayoutHistory } from '@/api/finance/moneyManagement'
import { centsToYuan, yuanToCents } from '@/utils/amount'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'
import { WITHDRAW_PENDING_STATUS_MAP, canWithdrawRefund } from './config'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'
import RiskControlInfoDialog from './RiskControlInfoDialog.vue'
import WithdrawRefundDialog from './WithdrawRefundDialog.vue'

export default {
  name: 'ManageWithdrawPendingTab',
  components: { MemberAccountCell, SchemaTablePage, RiskControlInfoDialog, WithdrawRefundDialog },
  filters: {
    centsToYuan,
  },
  data() {
    return {
      canWithdrawRefund,
      riskDialogVisible: false,
      riskRow: null,
      openFromAudit: false,
      refundDialogVisible: false,
      refundRow: null,
      defaultQuery: {
        memberAccount: '',
        orderNo: '',
        thirdOrderNo: '',
        minOrderAmount: undefined,
        maxOrderAmount: undefined,
        applyTimeRange: [],
        paymentChannelName: '',
      },
      searchSchema: [
        {
          prop: 'memberAccount',
          label: '会员账号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'orderNo',
          label: '订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'thirdOrderNo',
          label: '三方订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          key: 'amountRange',
          label: '订单金额',
          component: 'slot',
          slot: 'search-amountRange',
        },
        {
          prop: 'applyTimeRange',
          label: '申请时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          span: { xs: 24, sm: 24, md: 12, lg: 10 },
        },
        {
          prop: 'paymentChannelName',
          label: '代付通道',
          component: 'input',
          componentProps: { clearable: true },
        },
        { component: 'actions' },
      ],
      tableColumns: [
        { prop: 'orderNo', label: '订单号', minWidth: 180, resizable: false },
        { prop: 'thirdOrderNo', label: '三方订单号', minWidth: 200, resizable: false },
        { prop: 'status', label: '订单状态', width: 150, align: 'center', resizable: false, slot: 'colStatusText' },
        { prop: 'memberAccount', label: '会员账号', minWidth: 140, resizable: false, slot: 'colMemberAccount' },
        { prop: 'orderAmount', label: '订单金额', width: 100, resizable: false, slot: 'colOrderAmount' },
        { prop: 'accountType', label: '账号类型', width: 80, resizable: false },
        { prop: 'bankName', label: '银行名称', minWidth: 200, resizable: false },
        { prop: 'account', label: '收款账号', minWidth: 120, resizable: false },
        { prop: 'paymentChannelName', label: '代付通道', width: 80, resizable: false },
        { prop: 'applyTime', label: '申请时间', minWidth: 160, resizable: false },
        { prop: 'remark', label: '备注', minWidth: 140, resizable: false },
        {
          fixed: 'right',
          prop: 'action',
          label: '操作',
          minWidth: 120,
          align: 'center',
          resizable: false,
          slot: 'colActions',
        },
      ],
      tableAttrs: {
        size: 'mini',
        showSummary: true,
        summaryMethod: (...args) => this.getSummaries(...args),
      },
    }
  },
  methods: {
    request(params) {
      return getPayoutHistory(params)
    },
    buildParams({ query, page, limit }) {
      const params = {
        page,
        page_size: limit,
        status: [1, 2, 3, 4],
      }
      if (query.memberAccount) params.username = query.memberAccount
      if (query.orderNo) params.order_id = query.orderNo
      if (query.thirdOrderNo) params.third_order_id = query.thirdOrderNo
      if (query.minOrderAmount > 0) params.order_amount_start = yuanToCents(query.minOrderAmount)
      if (query.maxOrderAmount > 0) params.order_amount_end = yuanToCents(query.maxOrderAmount)
      if (query.paymentChannelName) params.payment_channel_name = query.paymentChannelName
      if (Array.isArray(query.applyTimeRange) && query.applyTimeRange.length === 2) {
        const [start, end] = query.applyTimeRange
        const startAt = toUnixSeconds(start)
        const endAt = toUnixSeconds(end)
        if (startAt) params.start_at = startAt
        if (endAt) params.end_at = endAt
      }
      return params
    },
    parseResult(res) {
      const respData = (res && res.data) || {}
      const list = respData.list || []
      return {
        list: list.map(item => this.mapWithdrawItem(item)),
        total: respData.total || 0,
      }
    },
    mapWithdrawItem(item) {
      const statusText =
        WITHDRAW_PENDING_STATUS_MAP[item.status] != null
          ? WITHDRAW_PENDING_STATUS_MAP[item.status]
          : `状态${item.status}`
      return {
        uid: item.uid,
        orderNo: item.order_id,
        thirdOrderNo: item.third_order_id || '',
        memberAccount: item.username || '',
        memberLevel: item.vip_level != null ? String(item.vip_level) : '',
        orderAmount: item.amount,
        paymentInfo: item.cpf || '',
        applyTime: formatTimestamp(item.created_at),
        appointmentDuration: '',
        estimatedArrivalTime: '',
        riskControlCompleteTime: formatTimestamp(item.operator_at),
        riskControlOperator: item.operator || '',
        status: item.status,
        statusText,
        account: item.account || '',
        remark: item.msg || item.remark || '',
        accountType: item.account_type || '',
        bankName: item.bank_name || '',
        paymentChannelName: item.payment_channel_name || '',
        action: '',
      }
    },
    openRefundDialog(row) {
      this.refundRow = row
      this.refundDialogVisible = true
    },
    handleRefundSuccess() {
      this.$refs.tablePageRef?.reload?.()
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        if (column.property === 'orderAmount') {
          const values = data.map(item => parseFloat(item[column.property]) || 0)
          sums[index] = values.reduce((prev, curr) => {
            const value = parseFloat(curr)
            if (!isNaN(value)) {
              return prev + curr
            }
            return prev
          }, 0)
          sums[index] = centsToYuan(sums[index])
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
    openRiskDialog(row, fromAudit = false) {
      this.riskRow = row
      this.openFromAudit = !!fromAudit
      this.riskDialogVisible = true
    },
    handleAudited() {
      this.$refs.tablePageRef?.reload?.()
    },
  },
}
</script>

<style scoped>
.amount-range {
  display: flex;
  align-items: center;
  width: 100%;
}

.amount-range .el-input-number {
  flex: 1;
  min-width: 0;
}
</style>
